import React, { useState, useEffect } from 'react';
import { scriptsService, TypeNFTs } from '@services/ScriptsService';
import { Empty, Spin, Avatar, Button } from 'antd';
import { MARKETPLACE_BUCKET_URL, MARKETPLACE_CDN_URL } from 'util/constants';
import { LoadingOutlined } from '@ant-design/icons';
import MarketplaceNFTCard from './MarketplaceNFTCard';
import LandNFTCard from './LandNFTCard';
import * as fcl from '@onflow/fcl';
import '@flow/config.js';
import { NFT } from './nft-types';
import { comingSoonWallets } from './flow-wallet-login/comingSoonWallets';
import styles from './nft-assets.module.css';
import Web3 from 'web3';
import { networks } from './networksConst';

type Props = {
  callback: Function;
  typeNFTs: TypeNFTs;
};

type Provider = {
  name: string;
  address: string;
  provider?: any;
};

type Service = {
  provider: Provider;
} | null;

type User = {
  loggedIn: boolean;
  addr: undefined | string;
  services: undefined | Service[];
};

declare global {
  interface Window {
    bitkeep?: any;
  }
}

const collectionsWhiteList = {
  NBA: 'NBATopShot',
  OZONE: 'OzoneMetaverseMarketplace',
  FLOVATAR: 'Flovatar',
  LALIGA: 'LaligaGolazos',
  UFC: 'UFCStrike',
  NFL: 'NFLAllDay',
};

const NftAssets: React.FC<Props> = ({ callback, typeNFTs }) => {
  const [userFlow, setUserFlow] = useState({
    loggedIn: false,
    addr: undefined,
    services: undefined,
  } as User);
  const [nfts, setNfts] = useState([] as NFT['marketplace'][] | NFT['land'][]);
  const [nftLoading, setNftLoading] = useState(false);

  useEffect(() => fcl.currentUser.subscribe(setUserFlow), []);

  useEffect(() => {
    async function getNft() {
      if (
        (!userFlow.loggedIn && !userFlow.addr) ||
        (userFlow as any).services[0].provider.name === 'Bitget Wallet'
      ) {
        return;
      }

      setNftLoading(true);
      let items: any[];
      let res = null;
      let nftCatalog = null as any;

      if ((userFlow as any).services[0].provider.name === 'Dapper Wallet') {
        try {
          nftCatalog = await scriptsService.GetAnyNftsFromAccount(
            userFlow.addr!
          );
        } catch (e) {
          console.error(e);
        }
      } else {
        try {
          res = await scriptsService.GetAllNftsFromAccount(
            userFlow.addr!,
            typeNFTs
          );
        } catch (e) {
          console.error(e);
        }
      }

      setNftLoading(false);

      if (!res && !nftCatalog && typeNFTs === 'marketplace') {
        setNfts([]);
        return;
      }

      if (res) {
        items = Object.values(res);
      } else {
        items = [];
      }

      if (typeNFTs === 'land') {
        try {
          const oldLand = await scriptsService.GetAllNftsFromAccount(
            userFlow.addr!,
            'oldLand'
          );

          if (oldLand) {
            const nfts = Object.values(oldLand);

            if (nfts.length) {
              items = items.concat(nfts);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      if (typeNFTs === 'marketplace') {
        let nftsOut: NFT['marketplace'][] = items as any;

        if (nftCatalog) {
          const nba = nftCatalog[collectionsWhiteList.NBA];
          const ufc = nftCatalog[collectionsWhiteList.UFC];
          const laliga = nftCatalog[collectionsWhiteList.LALIGA];
          const nfl = nftCatalog[collectionsWhiteList.NFL];
          const ozone = nftCatalog[collectionsWhiteList.OZONE];
          const flovatar = nftCatalog[collectionsWhiteList.FLOVATAR];

          if (nba?.length) {
            nba.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                format: 'VIDEO',
                previewImage: e?.thumbnail,
                externalUrl: e?.externalURL,
                id: e?.id,
                isExternal: true,
                name: e?.name,
              } as any);
            });
          }

          if (ufc?.length) {
            ufc.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                format: 'VIDEO',
                previewImage: e?.thumbnail,
                externalUrl: e?.externalURL,
                id: e?.id,
                isExternal: true,
                isUFC: true,
                name: e?.name,
              } as any);
            });
          }

          if (laliga?.length) {
            laliga.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                format: 'VIDEO',
                previewImage: e?.thumbnail,
                externalUrl: e?.externalURL,
                id: e?.id,
                isExternal: true,
                isLALIGA: true,
                name: e?.name,
              } as any);
            });
          }

          if (nfl?.length) {
            nfl.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                format: 'VIDEO',
                previewImage: e?.thumbnail,
                externalUrl: e?.externalURL,
                id: e?.id,
                isExternal: true,
                isNFL: true,
                name: e?.name,
              } as any);
            });
          }

          if (ozone?.length) {
            ozone.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                previewImage: e?.thumbnail,
                id: e?.id,
                isOzone: true,
                isExternal: true,
                name: e?.name,
              } as any);
            });
          }

          if (flovatar?.length) {
            flovatar.forEach((e: any) => {
              nftsOut.push({
                fileIPFS: e?.thumbnail,
                format: 'IMAGE',
                previewImage: e?.thumbnail,
                id: e?.id,
                name: e?.name,
              } as any);
            });
          }
        } else {
          nftsOut = nftsOut.map((item: NFT['marketplace']) => {
            return {
              ...item,
              previewImage: item.previewImage.replace(
                MARKETPLACE_BUCKET_URL,
                MARKETPLACE_CDN_URL
              ),
              fileIPFS: item.fileIPFS.replace(
                MARKETPLACE_BUCKET_URL,
                MARKETPLACE_CDN_URL
              ),
            };
          });
        }
        setNfts(nftsOut);
        return;
      }

      setNfts(items as NFT['land'][]);
    }
    getNft();
  }, [userFlow.addr, typeNFTs]);

  const [services, setServices] = useState([] as Service[]);

  useEffect(
    () =>
      fcl.discovery.authn.subscribe((res) => {
        const listProvidersFCL = ['Blocto', 'Dapper Wallet'];
        const servicesOut = res.results?.filter((item) =>
          listProvidersFCL.includes(item?.provider?.name)
        );
        if (servicesOut?.length) {
          servicesOut.push({
            provider: {
              name: 'Bitget Wallet',
              provider: window.bitkeep?.ethereum,
              address: 'Bitget Wallet',
            },
          });
        }

        setServices(() => servicesOut || []);
      }),
    []
  );

  const login = async (serviceIn: Service = null) => {
    const name = serviceIn?.provider.name || '';

    switch (name) {
      case 'Blocto':
        await fcl.authenticate({ service: serviceIn });
        break;
      case 'Dapper Wallet':
        await fcl.authenticate({ service: serviceIn });
        break;
      case 'Bitget Wallet':
        const provider: any = serviceIn?.provider?.provider;

        if (!provider) {
          return window.open('https://bitkeep.com/en/download?type=2');
        }

        await provider.enable();
        const web3 = new Web3(provider);

        const bscNetwork = networks['BSC'];
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: bscNetwork.chainId }],
        });
        const accounts = await web3.eth.getAccounts();

        setUserFlow(() => {
          return {
            loggedIn: true,
            addr: accounts[0],
            services: [serviceIn],
          };
        });

        break;

      default:
        await fcl.authenticate();
        break;
    }
    return undefined;
  };

  const anotherAccount = async () => {
    await fcl.unauthenticate();
  };

  return (
    <div className={styles.root}>
      {!userFlow.loggedIn ? (
        <>
          <span className={styles.title}>Please login with your wallet:</span>
          <div className={styles.content}>
            <div className={styles.container}>
              {services?.length ? (
                <>
                  {services.map((service) => (
                    <div
                      className={styles.walletWrapper}
                      key={service?.provider.address}
                      onClick={() => login(service)}
                    >
                      <Avatar
                        src={
                          service?.provider.name === 'Blocto'
                            ? '/blocto-icon.webp'
                            : service?.provider.name === 'Dapper Wallet'
                            ? '/dapper-icon.png'
                            : '/bitget.png'
                        }
                        className={styles.iconWallet}
                      ></Avatar>
                      <span className={styles.titleWallet}>
                        {service?.provider.name}
                      </span>
                    </div>
                  ))}

                  <div>
                    <p className={styles.comingSoonTitle}>Coming soon:</p>
                    {comingSoonWallets.map((wallet) => (
                      <div
                        className={styles.comingSoonWalletWrapper}
                        key={wallet?.name}
                      >
                        <Avatar
                          src={wallet.src}
                          className={styles.iconWallet}
                        ></Avatar>
                        <span className={styles.titleWallet}>
                          {wallet.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                !userFlow.loggedIn && (
                  <div className={styles.loading}>
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 30 }} spin />
                      }
                    ></Spin>
                  </div>
                )
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <span className={styles.title}>
            {`Your ${typeNFTs[0].toUpperCase() + typeNFTs.slice(1)} NFT's:`}
          </span>
          <div className={styles.content}>
            <div className={styles.wrapperLogin}>
              <div className={styles.walletWrapperLoggedIn}>
                <Avatar
                  src={
                    (userFlow as any).services[0].provider.name === 'Blocto'
                      ? '/blocto-icon.webp'
                      : '/dapper-icon.png'
                  }
                  className={styles.iconWallet}
                ></Avatar>
                <span
                  className={styles.titleWallet}
                  style={{ lineHeight: '100%' }}
                >{`${userFlow.addr}`}</span>
              </div>
              {userFlow.loggedIn && (
                <Button
                  onClick={anotherAccount}
                  className={styles.logoutWallet}
                >
                  Change Wallet
                </Button>
              )}
            </div>
            {!nftLoading ? (
              <div className={styles.listNfts}>
                {nfts.map((nft, i) => {
                  if (typeNFTs === 'marketplace') {
                    return (
                      <MarketplaceNFTCard
                        key={nft.id}
                        nft={nft}
                        callback={callback}
                      />
                    );
                  }
                  return (
                    <LandNFTCard
                      key={`${nft.id}-${i}`}
                      nft={nft}
                      callback={callback}
                    />
                  );
                })}
                {!nfts.length && (
                  <Empty
                    style={{ marginTop: 30 }}
                    description="You don't have NFT's"
                  />
                )}
              </div>
            ) : (
              <div className={styles.loading}>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 30 }} spin />}
                ></Spin>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NftAssets;
